import { useState } from "react";
import { useSync } from "@/context/SyncContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

// Shown before an ordinary logout when the current account has pending evidence (queued blobs,
// memory-only captures, active uploads, or Same-View pairing-pending records). Ordinary logout
// NEVER silently deletes evidence.
export function LogoutGuardDialog() {
  const { logoutGuard, cancelLogout, keepAndLogout, removeAndLogout, queuedCount, memoryOnly, pairingPending, syncing } = useSync();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!logoutGuard?.open) return null;

  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); setConfirmRemove(false); } };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) { setConfirmRemove(false); cancelLogout(); } }}>
      <DialogContent data-testid="logout-guard-dialog" className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#92400E]">
            <AlertTriangle size={18} /> You have photos waiting to sync
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">You have photos waiting to sync. Choose whether to keep or remove them before logging out.</DialogDescription>
        <ul className="text-sm text-[#4B5563] list-disc pl-5 space-y-1">
          <li>Uploads will pause when you log out.</li>
          <li>Durably queued photos stay in this browser — sign back into the same account to resume them.</li>
          <li>Clearing browser or site data may remove them.</li>
          <li>Photos held only in memory are not safe after leaving unless uploaded, saved, or downloaded.</li>
        </ul>
        <div className="mt-1 text-xs text-[#6B7280]">
          {queuedCount || 0} queued · {(memoryOnly || []).length} memory-only · {(pairingPending || []).length} pairing pending{syncing ? " · syncing…" : ""}
        </div>

        {!confirmRemove ? (
          <div className="mt-3 space-y-2">
            <Button data-testid="logout-cancel-btn" onClick={cancelLogout} variant="outline" className="w-full rounded-xl" disabled={busy}>Cancel</Button>
            <Button data-testid="logout-keep-btn" onClick={() => run(keepAndLogout)} className="w-full rounded-xl bg-[#111827] hover:bg-black" disabled={busy}>Log out and keep pending photos</Button>
            <Button data-testid="logout-remove-btn" onClick={() => setConfirmRemove(true)} variant="outline" className="w-full rounded-xl border-[#FECACA] text-[#B91C1C]" disabled={busy}>Remove pending photos and log out</Button>
          </div>
        ) : (
          <div data-testid="logout-remove-confirm" className="mt-3 rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-3">
            <p className="text-sm text-[#7F1D1D] mb-2">Permanently delete this account's pending photos and pairing tasks from this browser? Uploaded evidence on the server is not affected. This cannot be undone.</p>
            <div className="flex gap-2">
              <Button data-testid="logout-remove-yes-btn" onClick={() => run(removeAndLogout)} className="flex-1 rounded-xl bg-[#B91C1C] hover:bg-[#991B1B]" disabled={busy}>Yes, delete & log out</Button>
              <Button data-testid="logout-remove-no-btn" onClick={() => setConfirmRemove(false)} variant="outline" className="flex-1 rounded-xl" disabled={busy}>Back</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
